import { InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import { addDays, format } from 'date-fns';
import { get } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { getDividends } from '../../../../http/PortfoliosApi';

const PAGE_SIZE = 10;

// The backend assumes payment this many days after the ex-date when the
// provider doesn't publish a pay date, which is most of the time.
const ASSUMED_PAY_DELAY_DAYS = 14;

const STATUS_CHIPS = {
  paid: { label: 'Paid', color: 'success' },
  announced: { label: 'Expected', color: 'info' },
};

export default function DividendsListComponent({
  portfolio,
  errMessage,
  setErrMessage,
  setSnackOpen,
}) {
  const [dividends, setDividends] = useState({ count: 0, items: [] });
  const [filterStatus, setFilterStatus] = useState('');
  const [page, setPage] = useState(0);

  const loadPage = useCallback(
    async (newPage) => {
      if (!portfolio?.id) return;
      try {
        const { data } = await getDividends(
          portfolio.id,
          newPage * PAGE_SIZE,
          PAGE_SIZE,
          filterStatus,
        );
        setDividends(data);
      } catch (err) {
        setErrMessage(get(err, 'response.data.data.message', errMessage));
        setSnackOpen(true);
      }
    },
    // errMessage is only the fallback text; reloading on it would refetch
    // every time an error is shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [portfolio?.id, filterStatus, setErrMessage, setSnackOpen],
  );

  useEffect(() => {
    // Reset to page 0 when filtering to avoid "empty" pages
    setPage(0);
    loadPage(0);
  }, [loadPage]);

  const handleChangePage = (event, newPage) => {
    loadPage(newPage);
    setPage(newPage);
  };

  const formatDate = (date) => (date ? format(new Date(date), 'dd/MM/yy') : '');

  const formatAmount = (amount, currency) => {
    if (amount == null || !currency) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(Number(amount));
  };

  const renderPayDate = (dividend) => {
    if (dividend.payDate) return formatDate(dividend.payDate);
    const assumed = addDays(new Date(dividend.exDate), ASSUMED_PAY_DELAY_DAYS);
    return (
      <Tooltip
        title={`No pay date published: assumed ${ASSUMED_PAY_DELAY_DAYS} days after the ex-date`}
      >
        <Box component="span" sx={{ fontStyle: 'italic' }}>
          {formatDate(assumed)}
        </Box>
      </Tooltip>
    );
  };

  const renderNetEUR = (dividend) => {
    // The exchange rate is fixed on payment, so expected ones have no EUR amount.
    if (dividend.status !== 'paid') return '—';
    const amount = formatAmount(dividend.netAmountEUR, 'EUR');
    if (dividend.cashCredited) return amount;
    return (
      <Tooltip title="Received before dividend tracking started: already included in the cash balance, not credited again">
        <Box
          component="span"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          <InfoOutlined fontSize="inherit" color="action" />
          {amount}
        </Box>
      </Tooltip>
    );
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filterStatus}
            label="Status"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="">
              <em>All</em>
            </MenuItem>
            <MenuItem value="announced">Expected</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
          </Select>
        </FormControl>
        <Button
          size="small"
          onClick={() => setFilterStatus('')}
          sx={{ textTransform: 'none' }}
        >
          Reset Filters
        </Button>
      </Stack>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Pay date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Ex-date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Asset</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Shares
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Gross
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Withholding
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Net
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">
                Net (EUR)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dividends?.items?.length > 0 ? (
              dividends.items.map((dividend) => (
                <TableRow key={dividend.id} hover>
                  <TableCell>{renderPayDate(dividend)}</TableCell>
                  <TableCell>{formatDate(dividend.exDate)}</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>
                    {dividend.companyName}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        STATUS_CHIPS[dividend.status]?.label ?? dividend.status
                      }
                      color={STATUS_CHIPS[dividend.status]?.color ?? 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{dividend.shares}</TableCell>
                  <TableCell align="right">
                    {formatAmount(dividend.grossAmount, dividend.currency)}
                  </TableCell>
                  <TableCell align="right">
                    {formatAmount(
                      dividend.withholdingAmount,
                      dividend.currency,
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                    {formatAmount(dividend.netAmount, dividend.currency)}
                  </TableCell>
                  <TableCell align="right">{renderNetEUR(dividend)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 3, color: 'text.secondary' }}
                >
                  No dividends recorded.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={dividends?.count || 0}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[]}
        page={page}
        onPageChange={handleChangePage}
        sx={{ borderTop: '1px solid divider' }}
      />
    </Box>
  );
}
